// EG-1641 Wedding Day.
// The form's structure (section order, titles, copy, limits) comes from the
// server on every load. Nothing about it is declared here.

// Free text is debounced, so a keystroke is never a request.
export const TEXT_DEBOUNCE_MS = 800;
// A short field typed into on a card, such as a venue or a phone number.
// Shorter than the long notes above: the answer is a few words, and the
// couple moves on from it quickly.
export const FIELD_DEBOUNCE_MS = 500;

// Face detection runs on the server after the upload, so the tags arrive late.
// Stops the poll on a photo the detector never finishes, without touching the
// answers already on screen.

// Two people share this form, so the partner's edits have to arrive without a
// reload. Only while the timeline is open: it is the section they both work in.
export const SHARED_POLL_MS = 20000;

// Section kinds this form knows how to render. The server sends the kind on
// each section, so the order and the copy stay its business, not ours.
export const KIND = {
  TIMELINE: 'wedding_timeline',
  FAMILY: 'family',
  NOTES: 'day_notes',
};

// The one moment the partner never reads. Everything else in Day Notes is
// the couple's shared planning, so the two sides see each other's.
export const SURPRISE = 'surprise';

// An event entry's timing state, as the server reports it. A pending entry is
// an edit request, and must never read as saved.
export const TIMING = {
  SAVED: 'saved',
  PENDING: 'pending_approval',
  UNSET: 'not_set',
};

// A family photo's server side detection state.
export const DETECTION = {
  PENDING: 'pending',
  RUNNING: 'running',
  DONE: 'done',
  FAILED: 'failed',
};

// Rejections the client acts on. The rest the server can send, such as
// unknown_section and invalid_payload, fall through to the generic failure
// strip with the server's own message.
export const REJECT = {
  SUBMITTED: 'brief_submitted',
  CONFLICT: 'timing_conflict',
  LIMIT: 'limit_exceeded',
  APPROVAL: 'needs_approval',
  NOT_YOURS: 'not_your_activity',
  // The estimation sold that event to one side, so its side is not the
  // couple's to change.
  SIDE_LOCKED: 'side_locked',
  // That event is already on the day for that side.
  DUPLICATE: 'duplicate_event',
  UNKNOWN_VENUE: 'unknown_venue',
  NO_WEDDING: 'no_wedding',
  INCOMPLETE: 'sections_incomplete',
  // Refusals the couple can act on where they are standing: a field still
  // to fill, a time the package fixed, a name that is not theirs to change,
  // a picture with nobody in it. Said against the thing refused, never as a
  // failure to save.
  ENTRY_INCOMPLETE: 'entry_incomplete',
  BEFORE_PACKAGE: 'before_package',
  AFTER_PACKAGE: 'after_package',
  INTERVIEW_OUTSIDE_PACKAGE: 'interview_outside_package',
  NAME_FIXED: 'name_fixed',
  // Twelve hours is a day of work, not one event.
  TOO_LONG: 'event_too_long',
  // Words where a link was asked for. The crew taps it to be driven there,
  // so a description of the place is no use.
  BAD_MAP_LINK: 'invalid_map_link',
  NO_FACES_FOUND: 'no_faces',
};

// Said when the detector found nobody. Detection is the couple's own device
// now, so a dark or distant photograph is the usual reason.
export const NO_FACES = 'We could not find a face in that picture. '
  + 'Try a brighter one, or one taken closer up.';
// A face is cut out of the photograph it was found on, and that photograph
// is only ever in memory, so picking one after a reload has nothing to cut.
export const NEEDS_PHOTO = 'Add that photograph again to pick a face from it.';

// Never discards local state: a failed write is retried on this ladder and the
// answer stays on screen either way.
export const RETRY_DELAYS = [1000, 3000, 8000];
// How long a request may hang before it counts as failed. Without one a
// server that never answers leaves the loader spinning for good. Moving a
// file gets longer than a call, because it carries the bytes themselves.
export const REQUEST_TIMEOUT_MS = 20000;
export const TRANSFER_TIMEOUT_MS = 60000;
// What each kind of write is, in the couple's words, for saying which answer
// has not saved. Matched on the front of the write key, so the longer of two
// that share a start comes first: a new note before a note, the interview
// answer before an interview day.
export const WRITE_LABELS = [
  ['activity:new:', 'a new Day Notes entry'],
  ['activity:', 'a Day Notes entry'],
  ['section:', 'your notes'],
  ['timing:', 'an event timing or venue'],
  ['confirmed:', 'an event you confirmed'],
  ['name:', 'a ritual name'],
  ['side:', 'whose side an event is'],
  ['order:', 'the order of events'],
  ['coupleshot:', 'a couple shot'],
  ['shotat:', 'a couple shot time'],
  ['interviewchoice', 'the interview answer'],
  ['interview:', 'an interview day'],
  ['contact:', 'a contact'],
  ['face:', 'a family face'],
  ['photo:', 'a family photograph'],
];

// A rejection the caller handles itself, so the generic failure strip stays
// down: the server turned a write into an edit request, or the message belongs
// against one control.
export const HANDLED_REJECTS = [
  REJECT.SUBMITTED, REJECT.APPROVAL, REJECT.CONFLICT, REJECT.LIMIT, REJECT.UNKNOWN_VENUE,
  REJECT.NOT_YOURS, REJECT.SIDE_LOCKED, REJECT.DUPLICATE,
  REJECT.ENTRY_INCOMPLETE, REJECT.BEFORE_PACKAGE, REJECT.AFTER_PACKAGE,
  REJECT.INTERVIEW_OUTSIDE_PACKAGE,
  REJECT.NAME_FIXED, REJECT.BAD_MAP_LINK, REJECT.TOO_LONG,
  REJECT.NO_FACES_FOUND,
];

// Quarter hours, which is how a wedding day is planned. Event timings,
// interview slots and couple shots are all set on this grid. A time the
// schedule already holds off the grid is added to its own list, so nothing
// is lost.
export const STEP_MINUTES = 15;
// One event, not a day of them. The server refuses anything longer, so the
// picker stops offering it rather than letting them pick a refusal.
export const MAX_EVENT_MINUTES = 18 * 60;
// A day that closes by this hour has run past midnight, and starts before
// it are that night's tail rather than the next morning's events.
export const SMALL_HOURS_END = 6 * 60;
// The minute marks a time picker offers.
export const QUARTERS = [0, 15, 30, 45];

// Whose an event, a family or an interview is, as the server spells it.
export const SIDE = { BRIDE: 'bride', GROOM: 'groom', BOTH: 'both' };

export const SIDE_LABEL = {
  [SIDE.BRIDE]: 'Bride side',
  [SIDE.GROOM]: 'Groom side',
  [SIDE.BOTH]: 'Both sides',
};

// The side tag on an event card, and the shorter one on the order list.
export const TIMING_SIDE_CHIP = { [SIDE.BRIDE]: 'BRIDE SIDE', [SIDE.GROOM]: 'GROOM SIDE', [SIDE.BOTH]: 'BOTH SIDES' };
export const ORDER_SIDE_CHIP = { [SIDE.BRIDE]: 'BRIDE', [SIDE.GROOM]: 'GROOM', [SIDE.BOTH]: 'BOTH' };

export const SIDE_WORD = { [SIDE.BRIDE]: 'the bride', [SIDE.GROOM]: 'the groom' };

// The choices for whose an event is, and for whose family is being filled in.
export const EVENT_SIDES = [SIDE.BRIDE, SIDE.GROOM, SIDE.BOTH].map((value) => ({ value, label: SIDE_LABEL[value] }));
export const FAMILY_SIDES = [SIDE.BRIDE, SIDE.GROOM].map((value) => ({ value, label: SIDE_LABEL[value] }));

// An event the couple added themselves, rather than one sold on the estimation.
export const RITUAL = 'ritual';
// One the estimation sold. The server's own word for it, beside RITUAL.
export const PACKAGE = 'package';

// The kind tag on an event card, and the shorter one the order list uses on
// a phone, the same pair as the side tags above.
export const KIND_CHIP = { [PACKAGE]: 'PACKAGE EVENT', [RITUAL]: 'ADDED RITUAL' };
export const KIND_CHIP_SHORT = { [PACKAGE]: 'PACKAGE', [RITUAL]: 'RITUAL' };

// The interview the couple sit together, which belongs to neither side.
export const COUPLE_GROUP = 'couple';

// Every contact field the form actually puts on screen. A backup is never
// asked who they are, so asking for it here would hold the section open on a
// question the couple is not shown.
export const CONTACT_FIELDS = {
  coordinator: ['name', 'phone', 'relationship'],
  backup: ['name', 'phone'],
};

// A crop is a thumbnail beside a name, so it is stored at the size it is
// looked at rather than at whatever the camera happened to shoot.
export const CROP_EDGE = 320;
// The box is drawn tight around the face. A little room around it is the
// difference between a portrait and a cut out nose.
export const CROP_MARGIN = 0.35;
export const CROP_TYPE = 'image/jpeg';
export const CROP_QUALITY = 0.9;
// A detection box stops at the jaw, which reads as a crop of a chin rather
// than of a person, so the tile takes this much again around it.
export const CROP_PAD = 1.7;

// YuNet, the face detector: its input size and the strides of its three
// feature maps.
export const YUNET_INPUT_SIZE = 640;
export const YUNET_STRIDES = [8, 16, 32];
// Below this a face is too uncertain to be worth asking the couple about.
export const YUNET_SCORE_MIN = 0.6;
// OpenCV's own default for this model.
export const YUNET_NMS_IOU = 0.3;

// The brief on the deep link: 32 hex characters, dashes aside. Anything else
// is a link that was cut short or edited, so the server is not asked about it.
export const BRIEF_PATTERN = /^[0-9a-f]{32}$/i;
// Shown in place of the form when the link carries no brief, or one that is
// malformed or unknown to the server.
export const INVALID_LINK = 'This link is not valid. Please open the form from the link you were sent.';

// Below this width the form takes its phone layout. Keep in step with the
// `@media (max-width: 619px)` rule in WeddingDay.css.
export const PHONE_MAX_PX = 620;
